import { forwardRef } from 'react';

import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import CircleUserIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleUser';
import ClipboardListIcon from '@liquidweb-dev/undercurrent/icons/lucide/ClipboardList';
import GlobeLockIcon from '@liquidweb-dev/undercurrent/icons/lucide/GlobeLock';
import HistoryIcon from '@liquidweb-dev/undercurrent/icons/lucide/History';
import KeyIcon from '@liquidweb-dev/undercurrent/icons/lucide/KeyRound';
import ListIcon from '@liquidweb-dev/undercurrent/icons/lucide/List';
import ListChecksIcon from '@liquidweb-dev/undercurrent/icons/lucide/ListChecks';
import ReceiptIcon from '@liquidweb-dev/undercurrent/icons/lucide/Receipt';
import SettingsIcon from '@liquidweb-dev/undercurrent/icons/lucide/Settings';
import Settings2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Settings2';
import SquareAsteriskIcon from '@liquidweb-dev/undercurrent/icons/lucide/SquareAsterisk';
import UsersIcon from '@liquidweb-dev/undercurrent/icons/lucide/Users';
import WalletCardsIcon from '@liquidweb-dev/undercurrent/icons/lucide/WalletCards';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import NavLink from 'components/NavLinkSub';
import PageLayout, { PageLayoutInsetContent } from 'components/PageLayout';
import { IntroTarget, introTargets } from 'components/introjs';
import { useNavTitle } from 'components/LeftHandLayout';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';
import paths from './paths';
import SmallNav from './SmallNav';

/**
 * Provides some extra padding for the intro helperLayer but negates the padding
 * with negative margin to keep children in the same position
 */
const IntroWrapper = forwardRef(({ sx, ...rest }: BoxProps, ref) => (
	<Box
		sx={combineSx(
			{
				paddingX: 150,
				marginX: -150,
				paddingTop: 150,
				marginTop: -150,
				paddingBottom: 50,
				marginBottom: -50,
			},
			sx,
		)}
		ref={ref}
		{...rest}
	/>
));

IntroWrapper.displayName = 'IntroWrapper';

const NavSectionLabel = ({ children }: { children: React.ReactNode }) => {
	return (
		<Typography
			variant="label-sm"
			sx={{
				color: ({ vars }) => vars.palette.uc.text.weak,
				paddingX: 150,
				textTransform: 'uppercase',
			}}
		>
			{children}
		</Typography>
	);
};

const AccountLayout = ({ children }: { children: React.ReactNode }) => {
	useNavTitle('Account settings');
	useDocumentTitle('Account settings');
	const { pathname } = useLocation();
	const userProfilePath = useSelector(appConfigSelectors.getUserProfilePath);

	return (
		<PageLayout title="Account settings" Icon={SettingsIcon}>
			<PageLayoutInsetContent
				sx={{
					flexDirection: { xs: 'column', md: 'row' },
					gap: 600,
					flexGrow: 1,
				}}
			>
				<Stack
					component="nav"
					sx={{
						gap: 300,
						flex: '1 0 200px',
						display: { xs: 'none', md: 'flex' },
					}}
				>
					<IntroTarget introId={introTargets.accountSubNavGeneral}>
						<IntroWrapper>
							<Stack sx={{ gap: 100 }}>
								<NavSectionLabel>General</NavSectionLabel>

								<Stack sx={{ gap: 50 }}>
									<NavLink
										active={pathname === paths.accountDetails}
										component={RouterLink}
										startIcon={<ListIcon />}
										// @ts-expect-error
										to={paths.accountDetails}
									>
										Details
									</NavLink>

									<NavLink
										active={pathname === paths.accountPreferences}
										component={RouterLink}
										startIcon={<Settings2Icon />}
										// @ts-expect-error
										to={paths.accountPreferences}
									>
										Preferences
									</NavLink>

									<NavLink
										component="a"
										href={userProfilePath}
										startIcon={<CircleUserIcon />}
										endIcon={<ArrowUpRightIcon />}
										sx={{ cursor: 'pointer' }}
										target="_blank"
									>
										Profile
									</NavLink>

									<NavLink
										active={pathname === paths.accountUsers}
										component={RouterLink}
										startIcon={<UsersIcon />}
										// @ts-expect-error
										to={paths.accountUsers}
									>
										Users
									</NavLink>

									<NavLink
										active={pathname === paths.accountActivity}
										component={RouterLink}
										startIcon={<HistoryIcon />}
										// @ts-expect-error
										to={paths.accountActivity}
									>
										Activity
									</NavLink>

									<NavLink
										active={pathname === paths.accountAllServices}
										component={RouterLink}
										startIcon={<ClipboardListIcon />}
										// @ts-expect-error
										to={paths.accountAllServices}
									>
										All Services
									</NavLink>
								</Stack>
							</Stack>
						</IntroWrapper>
					</IntroTarget>

					<IntroTarget introId={introTargets.accountSubNavBilling}>
						<IntroWrapper>
							<Stack sx={{ gap: 100 }}>
								<NavSectionLabel>Billing</NavSectionLabel>

								<Stack sx={{ gap: 50 }}>
									<NavLink
										active={pathname === paths.billingOverview}
										component={RouterLink}
										startIcon={<WalletCardsIcon />}
										// @ts-expect-error
										to={paths.billingOverview}
									>
										Billing overview
									</NavLink>

									<NavLink
										active={pathname === paths.billingTransactions}
										component={RouterLink}
										startIcon={<ReceiptIcon />}
										// @ts-expect-error
										to={paths.billingTransactions}
									>
										Transactions
									</NavLink>
								</Stack>
							</Stack>
						</IntroWrapper>
					</IntroTarget>

					<IntroTarget introId={introTargets.accountSubNavSecurity}>
						<IntroWrapper>
							<Stack sx={{ gap: 100 }}>
								<NavSectionLabel>Security</NavSectionLabel>

								<Stack sx={{ gap: 50 }}>
									<NavLink
										active={pathname === paths.apiTokens}
										component={RouterLink}
										startIcon={<SquareAsteriskIcon />}
										// @ts-expect-error
										to={paths.apiTokens}
									>
										API tokens
									</NavLink>

									<NavLink
										active={pathname === paths.cloudVpn}
										component={RouterLink}
										startIcon={<GlobeLockIcon />}
										// @ts-expect-error
										to={paths.cloudVpn}
									>
										Cloud VPN users
									</NavLink>

									<NavLink
										active={pathname === paths.sshKeys}
										component={RouterLink}
										startIcon={<KeyIcon />}
										// @ts-expect-error
										to={paths.sshKeys}
									>
										SSH Keys
									</NavLink>

									<NavLink
										active={pathname === paths.compliance}
										component={RouterLink}
										startIcon={<ListChecksIcon />}
										// @ts-expect-error
										to={paths.compliance}
									>
										Compliance
									</NavLink>
								</Stack>
							</Stack>
						</IntroWrapper>
					</IntroTarget>
				</Stack>

				<SmallNav sx={{ display: { xs: 'block', md: 'none' } }} />

				<Stack sx={{ width: '100%', overflow: 'auto' }}>{children}</Stack>
			</PageLayoutInsetContent>
		</PageLayout>
	);
};

export default AccountLayout;
